import { useState } from "react";
import { ChevronDown, ChevronRight, Ship } from "lucide-react";

const ShipLoadingStatus = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const analysisMetrics = [
    {
      id: "ballast",
      title: "Ballast Status",
      description: "Vessel traveling without cargo - higher fuel consumption per distance",
      calculation: "Hourly allocation based on VesselStatus loadCondition = 'Ballast'",
      calculationDetails: [
        "Data Source: VesselStatus collection → loadCondition field",
        "Status Values: 'Ballast' or 'Laden' only (filtered by $in operator)",
        "Hour Counting: Each hour assigned to Ballast or Laden based on status",
        "Aggregation: Daily total Ballast hours calculated",
        "Purpose: Track fuel-inefficient operational periods"
      ],
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "laden",
      title: "Laden Status",
      description: "Vessel traveling with cargo - optimal fuel efficiency when fully loaded",
      calculation: "Hourly allocation based on VesselStatus loadCondition = 'Laden'",
      calculationDetails: [
        "Data Source: VesselStatus collection → loadCondition field",
        "Status Values: 'Laden' or 'Ballast' only (filtered by $in operator)",
        "Hour Counting: Each hour assigned to Laden or Ballast based on status",
        "Aggregation: Daily total Laden hours calculated",
        "Purpose: Track cargo-carrying operational efficiency"
      ],
      borderColor: "border-teal-200",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700",
    },
  ];

  const dataSourcesInfo = {
    title: "Primary Data Sources",
    description: "Loading status analysis relies on these key data collections:",
    sources: [
      {
        name: "VesselStatus",
        purpose: "Primary source of vessel loading status records",
        keyFields: "vessel, loadCondition, statusTime, voyageId",
        filter: "loadCondition ∈ ['Ballast', 'Laden']"
      },
      {
        name: "VesselVoyage",
        purpose: "Voyage information for voyage-based analysis",
        keyFields: "_id, voyageId, vessel, departTime, eta",
        usage: "Links VesselStatus records to specific voyages"
      },
      {
        name: "Vessel",
        purpose: "Vessel information and validation",
        keyFields: "_id, name, vessel identification",
        usage: "Validates vessel exists and belongs to user"
      }
    ]
  };

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#b6faed] rounded-lg">
              <Ship className="w-6 h-6 text-[#117864]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Ship Loading Status Analysis
              </h2>
              <p className="text-gray-600 text-sm">
                Hourly Ballast vs Laden status tracking and voyage/date range analysis
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            {/* Data Sources Information */}
            <div className="mb-6 p-4 bg-teal-50 border border-teal-200 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-2">{dataSourcesInfo.title}</h3>
              <p className="text-gray-600 text-sm mb-3">{dataSourcesInfo.description}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {dataSourcesInfo.sources.map((source, index) => (
                  <div key={index} className="p-3 bg-white rounded border border-teal-100">
                    <h4 className="font-semibold text-gray-700 text-sm mb-2">{source.name}</h4>
                    <p className="text-gray-600 text-xs mb-2">{source.purpose}</p>
                    <div className="space-y-1">
                      <p className="text-gray-500 text-xs font-mono">{source.keyFields}</p>
                      {source.filter && <p className="text-gray-500 text-xs italic">Filter: {source.filter}</p>}
                      {source.usage && <p className="text-gray-500 text-xs italic">Usage: {source.usage}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Analysis Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-5">
              {analysisMetrics.map((metric) => (
                <div
                  key={metric.id}
                  className={`p-5 rounded-lg border ${metric.borderColor} ${metric.bgColor}`}
                >
                  <h4 className="font-semibold text-gray-800 mb-3">
                    {metric.title}
                  </h4>
                  
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Description:</p>
                    <p className={`text-sm ${metric.textColor}`}>
                      {metric.description}
                    </p>
                  </div>

                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Calculation:</p>
                    <div className="p-2 bg-white rounded border border-gray-200">
                      <p className={`font-mono text-sm ${metric.textColor}`}>
                        {metric.calculation}
                      </p>
                    </div>
                  </div>

                  {metric.calculationDetails && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Implementation Details:</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {metric.calculationDetails.map((detail, index) => (
                          <li key={index}>• {detail}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShipLoadingStatus;